import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, GitBranch, Eye, ShieldCheck, Layers } from "lucide-react";
import ConsoleLayout from "@/components/equilibrium/ConsoleLayout";
import { fetchEqModules } from "@/lib/eq";

const ICONS = {
  spyglass: Eye,
  atlasbridge: GitBranch,
  horizon: Activity,
  dutydeck: ShieldCheck,
  oli: Layers,
};

export default function ConsoleModules() {
  const [mods, setMods] = useState([]);

  useEffect(() => {
    fetchEqModules().then(setMods).catch(() => setMods([]));
  }, []);

  return (
    <ConsoleLayout>
      <div data-testid="eq-console-modules">
        <div className="num-label">MODULE · SETTINGS</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl text-axiom-text font-bold tracking-tight">
          Installed modules.
        </h1>
        <p className="mt-2 text-axiom-muted text-sm max-w-2xl">
          Each module declares its inputs, outputs, telemetry, and operational status. Role-based
          access keys can be issued per module from this surface.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {mods.map((m) => {
            const Icon = ICONS[m.key] || Activity;
            return (
              <div
                key={m.key}
                data-testid={`eq-modset-${m.key}`}
                className="bg-axiom-bg p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 border border-axiom-accent/40 flex items-center justify-center">
                    <Icon size={18} className="text-axiom-accent" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-xl text-axiom-text font-bold">{m.name}</div>
                      <span className="num-label">{m.version}</span>
                    </div>
                    <div className="num-label mt-1">{m.domain}</div>
                  </div>
                  <StatusPill status={m.status} />
                </div>

                <p className="mt-4 text-sm text-axiom-muted leading-relaxed line-clamp-3">
                  {m.purpose}
                </p>

                <div className="mt-5 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                  <Cell k="CHANNELS" v={m.telemetry.channels} />
                  <Cell k="EVT/HR" v={m.telemetry.events_per_hour.toLocaleString()} />
                  <Cell k="COH%" v={`${m.telemetry.coherence_pct}%`} accent />
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1 max-w-[60%]">
                    {m.inputs.slice(0, 3).map((i) => (
                      <span
                        key={i}
                        className="text-[10px] font-mono uppercase border border-white/10 text-axiom-muted px-2 py-1"
                      >
                        {i}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/equilibrium/modules/${m.key}`}
                    className="btn-axiom-outline text-xs"
                  >
                    Open spec →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 num-label">
          Future modules will register here via the AtlasBridge module-registry contract.
        </div>
      </div>
    </ConsoleLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    OPERATIONAL: "border-emerald-400/40 text-emerald-300",
    PROVISIONAL: "border-amber-400/40 text-amber-300",
    FOUNDATIONAL: "border-axiom-accent/40 text-axiom-accent",
  };
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5 ${
        map[status] || "border-white/15 text-axiom-muted"
      }`}
    >
      {status}
    </span>
  );
}

function Cell({ k, v, accent }) {
  return (
    <div className="bg-axiom-bg p-3">
      <div className="num-label">{k}</div>
      <div
        className={`mt-1 font-display text-lg ${accent ? "text-axiom-accent" : "text-axiom-text"}`}
      >
        {v}
      </div>
    </div>
  );
}
